import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { MapPin, Calendar, ArrowRight, ChevronDown, Star, Check } from "lucide-react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import LatestBlogTeaser from "./LatestBlogTeaser";
import { ROBOTS_META_CONTENT } from "../lib/indexing";

const SITE_URL = "https://www.bilan2vie.fr";
const BUSINESS_ADDR = {
  "@type": "PostalAddress",
  streetAddress: "20 quai du Roubion",
  addressLocality: "Montélimar",
  postalCode: "26200",
  addressRegion: "Drôme",
  addressCountry: "FR",
};

/**
 * Reusable SEO-optimised local landing page (Voyance / Pranic Healing / Numérologie Montélimar).
 *
 * Props:
 *   - path: route path (used for canonical + JSON-LD URL)
 *   - service: { name, slug, kicker, h1, lead, image, priceFrom }
 *   - meta: { title, description, keywords }
 *   - about: array of paragraphs (rich textual content for SEO)
 *   - audience: array of "à qui ça s'adresse" bullets
 *   - steps: array of { title, desc } describing the séance flow
 *   - reasons: array of "pourquoi Bilan 2 Vie" bullets (geo + diff)
 *   - faqs: array of { q, a } for FAQPage JSON-LD
 *   - blogCategory: which blog category to link to
 */
export default function LocalSeoPage({ path, service, meta, about, audience, steps, reasons, faqs, blogCategory }) {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(0);

  // JSON-LD: LocalBusiness + Service offered + FAQPage
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "@id": SITE_URL + "/#localbusiness",
      name: "Bilan 2 Vie",
      description: meta.description,
      image: SITE_URL + "/logo.png",
      address: BUSINESS_ADDR,
      telephone: "+33 7 68 48 90 56",
      url: SITE_URL,
      areaServed: [
        { "@type": "City", name: "Montélimar" },
        { "@type": "City", name: "Valence" },
        { "@type": "City", name: "Avignon" },
        { "@type": "AdministrativeArea", name: "Drôme" },
        { "@type": "AdministrativeArea", name: "Ardèche" },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "Service",
      serviceType: service.name,
      provider: {
        "@type": "LocalBusiness",
        name: "Bilan 2 Vie",
        address: BUSINESS_ADDR,
      },
      areaServed: { "@type": "City", name: "Montélimar" },
      url: SITE_URL + path,
      offers: service.priceFrom
        ? {
            "@type": "Offer",
            price: String(service.priceFrom),
            priceCurrency: "EUR",
            availability: "https://schema.org/InStock",
          }
        : undefined,
    },
    faqs && faqs.length > 0
      ? {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map(({ q, a }) => ({
            "@type": "Question",
            name: q,
            acceptedAnswer: { "@type": "Answer", text: a },
          })),
        }
      : null,
  ].filter(Boolean);

  return (
    <main className="pt-24 min-h-screen overflow-x-hidden bg-[#1B132B]" data-testid={`local-seo-${service.slug}`}>
      <Helmet>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <meta name="keywords" content={meta.keywords} />
        <meta name="robots" content={ROBOTS_META_CONTENT} />
        <link rel="canonical" href={SITE_URL + path} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={SITE_URL + path} />
        {service.image && <meta property="og:image" content={service.image} />}
        {jsonLd.map((obj, i) => (
          <script key={i} type="application/ld+json">{JSON.stringify(obj)}</script>
        ))}
      </Helmet>

      {/* HERO */}
      <section className="relative py-16 md:py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#3A115E]/30 via-[#1B132B] to-[#1B132B]" />
        <div className="max-w-5xl mx-auto px-6 relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="flex items-center gap-2 text-[#00E5FF] text-xs uppercase tracking-widest mb-4">
              <MapPin className="w-4 h-4" /> {service.kicker}
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white mb-6 leading-tight" data-testid="local-seo-h1">
              {service.h1}
            </h1>
            <p className="text-lg text-[#E2D5F8] max-w-3xl leading-relaxed mb-8">{service.lead}</p>
            <div className="flex flex-wrap gap-4">
              <Button
                onClick={() => navigate("/booking")}
                className="gradient-primary text-base px-7 py-6 rounded-full hover:shadow-[0_0_30px_rgba(217,38,169,0.5)] transition-all"
                data-testid="local-seo-cta-booking"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Réserver une séance
              </Button>
              <Link to="/services">
                <Button
                  variant="outline"
                  className="border-[#00E5FF]/40 text-[#00E5FF] text-base px-7 py-6 rounded-full hover:bg-[#00E5FF]/10"
                  data-testid="local-seo-cta-services"
                >
                  Voir toutes les prestations <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
            <div className="mt-6 flex items-center gap-4 text-sm text-[#B59ED1]">
              <span className="inline-flex items-center gap-1">
                <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" /> 4,9/5 sur 139 avis Google
              </span>
              <span>•</span>
              <span>20 quai du Roubion, 26200 Montélimar</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CONTENT — "Qu'est-ce que" */}
      <section className="py-16 relative">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="font-serif text-3xl md:text-4xl text-white mb-8">{service.aboutTitle || `Qu'est-ce que ${service.name.toLowerCase()} ?`}</h2>
          <div className="space-y-5 text-[#E2D5F8] leading-relaxed">
            {about.map((para, i) => (
              <p key={i}>{para}</p>
            ))}
          </div>
        </div>
      </section>

      {/* AUDIENCE — "À qui ça s'adresse" */}
      <section className="py-16 relative bg-[#231839]/40">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="font-serif text-3xl md:text-4xl text-white mb-4">{service.audienceTitle || `À qui s'adresse cette prestation à Montélimar ?`}</h2>
          <p className="text-[#B59ED1] mb-10 max-w-2xl">
            Que tu sois de Montélimar, de la Drôme ou des départements alentours (Ardèche, Vaucluse, Gard), cette prestation peut t'apporter des réponses.
          </p>
          <div className="grid sm:grid-cols-2 gap-4">
            {audience.map((item, i) => (
              <div key={i} className="flex items-start gap-3 p-4 bg-[#261B3B] rounded-xl border border-white/10" data-testid={`audience-item-${i}`}>
                <Check className="w-5 h-5 text-[#00E5FF] flex-shrink-0 mt-0.5" />
                <span className="text-[#E2D5F8] text-sm">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* STEPS — "Comment se déroule une séance" */}
      <section className="py-16 relative">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="font-serif text-3xl md:text-4xl text-white mb-10">Comment se déroule une séance ?</h2>
          <div className="space-y-6">
            {steps.map((s, i) => (
              <div key={i} className="flex gap-5 p-6 bg-[#261B3B] rounded-2xl border border-white/10 hover:border-[#D926A9]/40 transition-colors" data-testid={`step-${i}`}>
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-gradient-to-br from-[#D926A9] to-[#3A115E] flex items-center justify-center font-serif text-xl text-white">
                  {i + 1}
                </div>
                <div>
                  <h3 className="font-serif text-xl text-white mb-2">{s.title}</h3>
                  <p className="text-[#B59ED1] text-sm leading-relaxed">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* REASONS — "Pourquoi Bilan 2 Vie à Montélimar" */}
      <section className="py-16 relative bg-[#231839]/40">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="font-serif text-3xl md:text-4xl text-white mb-4">Pourquoi choisir Bilan 2 Vie pour {service.shortName || service.name.toLowerCase()} à Montélimar ?</h2>
          <p className="text-[#B59ED1] mb-10 max-w-2xl">
            Cabinet situé en plein centre, à deux pas du parking gratuit du Roubion. Accessible depuis toute la Drôme provençale et l'Ardèche méridionale.
          </p>
          <div className="grid md:grid-cols-2 gap-5">
            {reasons.map((r, i) => (
              <div key={i} className="p-5 bg-[#261B3B] rounded-xl border border-white/10" data-testid={`reason-${i}`}>
                <div className="flex items-start gap-3">
                  <Badge className="bg-[#00E5FF]/20 text-[#00E5FF] flex-shrink-0 mt-1">0{i + 1}</Badge>
                  <p className="text-[#E2D5F8] text-sm leading-relaxed">{r}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      {faqs && faqs.length > 0 && (
        <section className="py-16 relative">
          <div className="max-w-3xl mx-auto px-6">
            <h2 className="font-serif text-3xl md:text-4xl text-white mb-10 text-center">Questions fréquentes</h2>
            <div className="space-y-3">
              {faqs.map((faq, i) => (
                <div key={i} className="bg-[#261B3B] rounded-xl border border-white/10 overflow-hidden" data-testid={`faq-${i}`}>
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                    className="w-full flex items-center justify-between gap-4 p-5 text-left hover:bg-[#3A115E]/40 transition-colors"
                    aria-expanded={openFaq === i}
                  >
                    <span className="font-serif text-lg text-white">{faq.q}</span>
                    <ChevronDown className={`w-5 h-5 text-[#00E5FF] flex-shrink-0 transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
                  </button>
                  {openFaq === i && (
                    <div className="p-5 pt-0 text-[#E2D5F8] text-sm leading-relaxed border-t border-white/5">
                      {faq.a}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-20 relative">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="p-10 rounded-3xl bg-gradient-to-r from-[#D926A9]/20 via-[#3A115E]/30 to-[#00E5FF]/10 border border-[#D926A9]/30">
            <h2 className="font-serif text-3xl md:text-4xl text-white mb-4">Prêt à franchir le pas ?</h2>
            <p className="text-[#E2D5F8] mb-8 max-w-2xl mx-auto">
              Réserve ta séance en quelques clics. Disponibilités en présentiel à Montélimar (Drôme) ou à distance partout en France.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Button
                onClick={() => navigate("/booking")}
                className="gradient-primary text-base px-8 py-6 rounded-full hover:shadow-[0_0_30px_rgba(217,38,169,0.5)] transition-all"
                data-testid="local-seo-final-cta"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Voir les disponibilités
              </Button>
              <a href="tel:+33768489056">
                <Button variant="outline" className="border-[#00E5FF]/40 text-[#00E5FF] text-base px-8 py-6 rounded-full hover:bg-[#00E5FF]/10">
                  Appeler le 07 68 48 90 56
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Related blog articles */}
      {blogCategory && (
        <LatestBlogTeaser
          title="Découvre le journal Bilan 2 Vie"
          subtitle="Articles spirituels, conseils pratiques et inspirations à explorer."
          category={blogCategory}
          limit={3}
        />
      )}
    </main>
  );
}
